import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/16px/icon_16px_x-circle_outline.svg';
import { ReactComponent as NavigationIcon } from '@repo/common/assets/icons/16px/icon_16px_navigation-right.svg';
import { ReactComponent as ReceiptIcon } from '@repo/common/assets/icons/16px/icon_16px_receipt_outline.svg';
import { ReactComponent as MailIcon } from '@repo/common/assets/icons/16px/icon_16px_mail_outline.svg';

import { BaseButton, useIsMobile, FilterBottomSheet, useT } from '@repo/common';

const SUPPORT_EMAIL = 'admin@ko-ready.com';

function WorkshopOptionList({
  anchorRect,
  onClose,
  onCancelClick,
  onViewInvoice,
  onViewBookingClick,
  label,
  booking,
}) {
  const t = useT();
  const listRef = useRef(null);
  const isMobile = useIsMobile();
  const [activeItem, setActiveItem] = useState(null);

  if (!anchorRect && !isMobile) return null;

  const handleItemClick = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveItem(action);

    if (action === 'cancel') handleCancelClick();
    if (action === 'contactSupport') handleContactSupportClick();
    if (action === 'viewInvoice') handleViewInvoiceClick();
    if (action === 'view') handleViewClick();
  };

  const handleCancelClick = () => {
    onCancelClick?.();
    onClose?.();
  };

  // 어드민 메일 작성 화면으로 이동 (예약 정보를 제목·본문에 미리 채운다)
  const handleContactSupportClick = () => {
    const subject = booking?.bookingSerialNumber
      ? `[Booking ${booking.bookingSerialNumber}] Support request`
      : 'Booking support request';
    const body = [
      booking?.workshopTitle ? `Workshop: ${booking.workshopTitle}` : '',
      booking?.bookingSerialNumber
        ? `Booking number: ${booking.bookingSerialNumber}`
        : '',
      booking?.date ? `Date: ${booking.date}` : '',
      '',
      'Please describe your request below:',
      '',
    ]
      .filter(Boolean)
      .join('\n');

    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;

    // window.location.href 는 메뉴가 곧바로 언마운트되면 취소되는 브라우저가 있어
    // 임시 앵커를 클릭해 확실히 메일 작성 화면을 연다
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    // 메일 클라이언트가 뜬 뒤에 메뉴를 닫는다
    setTimeout(() => onClose?.(), 0);
  };

  const handleViewInvoiceClick = () => {
    onViewInvoice?.();
    onClose?.();
  };

  const handleViewClick = () => {
    onViewBookingClick?.();
    onClose?.();
  };

  const menuItems = (
    <ul className='flex flex-col'>
      {label === 'Waitlist' && (
        <li>
          <BaseButton
            type='text'
            size='m'
            iconPosition='after'
            icon={<CancelIcon />}
            width='100%'
            separate={true}
            leftalign={true}
            onClick={(e) => handleItemClick(e, 'cancel')}
            bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
              activeItem === 'cancel'
                ? 'bg-grayscale-gray02 text-text-primary'
                : ''
            }`}
            textclass='typo-b14Strong'
            hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
            activeclass='active:bg-grayscale-gray02 active:text-text-primary'
          >
            {t('Cancel', '취소')}
          </BaseButton>
        </li>
      )}

      <li>
        <BaseButton
          type='text'
          size='m'
          iconPosition='after'
          icon={<MailIcon />}
          width='100%'
          separate={true}
          leftalign={true}
          onClick={(e) => handleItemClick(e, 'contactSupport')}
          bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
            activeItem === 'contactSupport'
              ? 'bg-grayscale-gray02 text-text-primary'
              : ''
          }`}
          textclass='typo-b14Strong'
          hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
          activeclass='active:bg-grayscale-gray02 active:text-text-primary'
        >
          {t('Contact support', '고객센터 문의')}
        </BaseButton>
      </li>

      {label !== 'Waitlist' && (
        <>
          <li>
            <BaseButton
              type='text'
              size='m'
              iconPosition='after'
              icon={<ReceiptIcon />}
              width='100%'
              separate={true}
              leftalign={true}
              onClick={(e) => handleItemClick(e, 'viewInvoice')}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'viewInvoice'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {t('View invoice', '영수증 보기')}
            </BaseButton>
          </li>

          <li>
            <BaseButton
              type='text'
              size='m'
              iconPosition='after'
              icon={<NavigationIcon />}
              width='100%'
              separate={true}
              leftalign={true}
              onClick={(e) => handleItemClick(e, 'view')}
              bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
                activeItem === 'view'
                  ? 'bg-grayscale-gray02 text-text-primary'
                  : ''
              }`}
              textclass='typo-b14Strong'
              hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
              activeclass='active:bg-grayscale-gray02 active:text-text-primary'
            >
              {t('View booking detail', '예약 상세 보기')}
            </BaseButton>
          </li>
        </>
      )}
    </ul>
  );

  return ReactDOM.createPortal(
    isMobile ? (
      <FilterBottomSheet onClose={onClose}>{menuItems}</FilterBottomSheet>
    ) : (
      anchorRect && (
        <>
          <div
            className='fixed inset-0 z-[999999] bg-transparent'
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
          />

          <div
            ref={listRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              top: anchorRect.top,
              left: anchorRect.left + 22,
            }}
            className='
              w-[200px]
              bg-grayscale-gray00
              border border-grayscale-gray02
              rounded-lg
              shadow-popup-04
              z-[1000000]
            '
          >
            <div className='p-2 rounded-lg bg-grayscale-gray00'>
              {menuItems}
            </div>
          </div>
        </>
      )
    ),
    document.body,
  );
}

export default WorkshopOptionList;
